import React, { useRef, useState, useEffect } from "react";
import { Container, Nav, NavItem, NavLink, Button, Spinner } from "reactstrap";
import classnames from "classnames";
import printLabelStyles from "../css/printlabel.module.scss";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import SubjectLabel from "../../SubjectLabel/pages/SubjectLabelList";
import SubjectICardLabel from "../../ICardLabel/pages/SubjectICardList";
import VacutainerLabelList from "../../VaccutainerLabel/pages/VacutainerLabelList";
import CryovialLabelList from "../../RiaLabel/pages/CryovialLabelList";
import DispensingLabelList from "../../DispensingLabel/pages/DispensingLabelList";
import { PRINT_HISTORY_PATHS } from "../../../../Routes/paths";
import CommonButton from "Components/Common/CommonButton";
import { PrintTabKey } from "../helper/printlist.constant";
import { PrintTabs } from "../helper/printlist.types";
import { PERMISSION_ACTIONS, PERMISSION_MODULES } from "common/constants/PermissionConstants";
import RouteGuard from "Routes/RouteGuard";
import { usePermission } from "context/PermissionContext";
import { useGlobalStudy } from "context/StudyContext";
import NoStudySelectedPlaceholder from "Components/Common/NoStudySelectedPlaceholder";
import { usePrintReportService } from "../api/usePrintReportService";
import { showToastMessage } from "common/service/CommonService";
import VacutainerReportModal from "../components/VacutainerReportModal";
import CryovialReportModal from "../components/CryovialReportModal";

/* ---------------- MAIN COMPONENT ---------------- */

const BARCODE_TYPE_MAP: Partial<Record<PrintTabKey, string>> = {
  subject: "SUBJECT",
  vacutainer: "VACUTAINER",
  cryovial: "ALIQUOT",
};

const PrintLogsTabs: React.FC = () => {
  const { hasPermission, isModuleVisible } = usePermission();
  const { selectedStudy } = useGlobalStudy();
  const { PrintReportSelectAllMutation } = usePrintReportService();
  const [isVacutainerModalOpen, setIsVacutainerModalOpen] = useState(false);
  const [isCryovialModalOpen, setIsCryovialModalOpen] = useState(false);
  const subject = hasPermission(PERMISSION_MODULES.SUBJECT_LABEL) && isModuleVisible("LABEL_SUBJECT");
  const icard = hasPermission(PERMISSION_MODULES.SUBJECT_ICARD_LABEL) && isModuleVisible("LABEL_ICARD");
  const vacut = hasPermission(PERMISSION_MODULES.VACUTAINER_LABEL) && isModuleVisible("LABEL_VACUTAINER");
  const cryo = hasPermission(PERMISSION_MODULES.RIAVIAL_LABEL) && isModuleVisible("LABEL_CRYOVIAL");
  const dis = hasPermission(PERMISSION_MODULES.DISPENSING_LABEL) && isModuleVisible("LABEL_DISPENSING");
  const permittedTabsList = PrintTabs(subject, icard, vacut, cryo, dis);

  const [activeTab, setActiveTab] = useState<PrintTabKey>(() => {
    return permittedTabsList[0]?.key || "subject";
  });
  const segmentTabsRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handlePrintReportClick = () => {
    if (activeTab === "vacutainer") {
      setIsVacutainerModalOpen(true);
      return;
    }
    if (activeTab === "cryovial") {
      setIsCryovialModalOpen(true);
      return;
    }
    const barcodeType = BARCODE_TYPE_MAP[activeTab];
    if (!barcodeType) return;
    executePrintReport({
      barcodetype: barcodeType,
      studyid: selectedStudy?.studyid ? Number(selectedStudy.studyid) : null,
    });
  };

  const executePrintReport = (payload: any) => {
    PrintReportSelectAllMutation.mutate(payload, {
      onSuccess: (data: any) => {
        setIsVacutainerModalOpen(false);
        setIsCryovialModalOpen(false);
        if (data instanceof Blob) {
          const blobUrl = URL.createObjectURL(data);
          window.open(blobUrl, "_blank");
        } else {
          showToastMessage("success", data?.message || "Report fetched successfully");
        }
      },
      onError: (err: any) => {
        showToastMessage("error", err?.message || err || "Failed to fetch print report");
      },
    });
  };

  /* ---------- Slider ---------- */

  const tabRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [slider, setSlider] = useState<{ left: string; width: string }>({
    left: "2px",
    width: "80px",
  });

  /* ---------- Sync tab from URL ---------- */

  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const last = segments[segments.length - 1];

    const match = permittedTabsList.find((t) => t.key === last);
    if (match) {
      setActiveTab(match.key);
    } else {
      const defaultTab = permittedTabsList[0];
      if (defaultTab) {
        setActiveTab(defaultTab.key);
        // Automatically redirect to first permitted tab path if we are on the base route
        const cleanPath = location.pathname.replace(/\/$/, "");
        if (cleanPath === "/print-labels") {
          navigate(defaultTab.key === "subject" ? "" : defaultTab.key, { replace: true });
        }
      } else {
        setActiveTab("subject");
      }
    }
  }, [location.pathname, subject, icard, vacut, cryo, dis, navigate]);

  /* ---------- Tab click ---------- */

  const toggleTab = (tabKey: PrintTabKey, index: number): void => {
    navigate(tabKey === "subject" ? "" : tabKey);
    setActiveTab(tabKey);

    const el = tabRefs.current[index];
    const padding = segmentTabsRef.current
      ? parseInt(getComputedStyle(segmentTabsRef.current).paddingLeft)
      : 0;

    if (el) {
      setSlider({
        left: el.offsetLeft + padding + "px",
        width: el.offsetWidth + "px",
      });
    }
  };

  /* ---------- Slider reposition ---------- */

  useEffect(() => {
    const index = PrintTabs(subject, icard, vacut, cryo, dis).findIndex((t) => t.key === activeTab);
    const el = tabRefs.current[index];

    const containerPadding = segmentTabsRef.current
      ? parseInt(getComputedStyle(segmentTabsRef.current).paddingLeft)
      : 0;

    if (el) {
      setSlider({
        left: el.offsetLeft + containerPadding + "px",
        width: el.offsetWidth + "px",
      });
    }
  }, [activeTab]);

  /* ---------------- RENDER ---------------- */

  const isDirectPrintLoading =
    !["vacutainer", "cryovial"].includes(activeTab) && PrintReportSelectAllMutation.isPending;

  return (
    <div className="page-content">
      {!selectedStudy?.studyid ? (
        <div>
          <NoStudySelectedPlaceholder moduleName="labels" />
        </div>
      ) : (
        <>
          <Container fluid className="pb-4">
            {/* Header Section (Custom for Print Label) */}
            <div className="d-flex justify-content-between align-items-center mt-2 mb-4 px-2">
              <div>
                <h4 className="fw-700 mb-1" style={{ color: "#0f4c5c" }}>Print Barcode Labels</h4>
                <p className="text-muted mb-0 small">
                  Generate and print standardized tracking labels for the clinical trial workflow.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2">
                {BARCODE_TYPE_MAP[activeTab] && (
                  <CommonButton
                    module={`${PERMISSION_MODULES.SUBJECT_LABEL},${PERMISSION_MODULES.VACUTAINER_LABEL},${PERMISSION_MODULES.RIAVIAL_LABEL}`}
                    action={PERMISSION_ACTIONS.VIEW}
                    color="primary"
                    outline
                    disabled={isDirectPrintLoading}
                    className="d-flex align-items-center gap-2 px-3 btn-sm fw-600"
                    onClick={handlePrintReportClick}
                  >
                    {isDirectPrintLoading ? (
                      <Spinner size="sm" className="me-1" />
                    ) : (
                      <i className="ri-file-text-line me-1" />
                    )}
                    Print Report
                  </CommonButton>
                )}
                {isModuleVisible("LABEL_REPRINT_HISTORY") && (
                  <CommonButton
                    module={`${PERMISSION_MODULES.SUBJECT_LABEL},${PERMISSION_MODULES.SUBJECT_ICARD_LABEL},${PERMISSION_MODULES.VACUTAINER_LABEL},${PERMISSION_MODULES.RIAVIAL_LABEL},${PERMISSION_MODULES.DISPENSING_LABEL}`}
                    action={`${PERMISSION_ACTIONS.VIEW},${PERMISSION_ACTIONS.ADD},${PERMISSION_ACTIONS.AUDIT},${PERMISSION_ACTIONS.FETCH_PRINT_STATUS},${PERMISSION_ACTIONS.PRINT}`}
                    color="primary"
                    outline
                    className="d-flex align-items-center gap-2 px-3 btn-sm fw-600"
                    onClick={() => {
                      const historyTabMap: Partial<Record<PrintTabKey, string>> = {
                        ...(subject ? { subject: "subject" } : {}),
                        ...(icard ? { icard: "idcard" } : {}),
                        ...(vacut ? { vacutainer: "vacutainer" } : {}),
                        ...(cryo ? { cryovial: "cryovial" } : {}),
                        ...(dis ? { dispensing: "dispensing" } : {}),
                      };
                      const targetTab = historyTabMap[activeTab];

                      if (!targetTab) return;

                      navigate(PRINT_HISTORY_PATHS.LIST, {
                        state: { activeTab: targetTab },
                      });
                    }}
                  >
                    <i className="ri-history-line me-1" /> Reprint History
                  </CommonButton>
                )}
              </div>
            </div>

            {/* Unified Card Container */}
            <div className="bg-white rounded-4 shadow-sm overflow-hidden border">

              {/* Tab Navigation Section */}
              <div className={`px-4 py-2 border-bottom ${printLabelStyles.tab_nav_section}`}>
                <Nav
                  innerRef={segmentTabsRef}
                  className={printLabelStyles.segmentTabs}
                  style={{ borderBottom: "none" }}
                >
                  <div
                    className={printLabelStyles.slider}
                    style={
                      {
                        "--left": slider.left,
                        "--width": slider.width,
                      } as React.CSSProperties
                    }
                  />

                  {PrintTabs(subject, icard, vacut, cryo, dis).map((t, index) => (
                    <NavItem key={t.key}>
                      <NavLink
                        innerRef={(el) => {
                          tabRefs.current[index] = el;
                        }}
                        className={classnames(printLabelStyles.tab, {
                          [printLabelStyles.active]: activeTab === t.key,
                        })}
                        onClick={() => {
                          toggleTab(t.key, index);
                        }}
                      >
                        <i className={`${t.icon} me-1 ${printLabelStyles.icon}`} />
                        <span>{t.label}</span>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              </div>

              {/* Tab Content Section */}
              <div className="p-4">
                <Routes>
                  <Route index element={<RouteGuard module={`${PERMISSION_MODULES.SUBJECT_LABEL},${PERMISSION_MODULES.SUBJECT_ICARD_LABEL},${PERMISSION_MODULES.VACUTAINER_LABEL},${PERMISSION_MODULES.RIAVIAL_LABEL},${PERMISSION_MODULES.DISPENSING_LABEL}`}><SubjectLabel /></RouteGuard>} />
                  <Route path="icard" element={<RouteGuard module={PERMISSION_MODULES.SUBJECT_ICARD_LABEL}><SubjectICardLabel /></RouteGuard>} />
                  <Route path="vacutainer" element={<RouteGuard module={PERMISSION_MODULES.VACUTAINER_LABEL}><VacutainerLabelList /></RouteGuard>} />
                  <Route path="cryovial" element={<RouteGuard module={PERMISSION_MODULES.RIAVIAL_LABEL}><CryovialLabelList /></RouteGuard>} />
                  <Route path="dispensing" element={<RouteGuard module={PERMISSION_MODULES.DISPENSING_LABEL}><DispensingLabelList /></RouteGuard>} />
                  <Route path="*" element={<RouteGuard module={PERMISSION_MODULES.SUBJECT_LABEL}><SubjectLabel /></RouteGuard>} />
                </Routes>
              </div>
            </div>
              </Container>

              <VacutainerReportModal
                isOpen={isVacutainerModalOpen}
                onClose={() => setIsVacutainerModalOpen(false)}
                selectedStudy={selectedStudy}
                onSubmit={executePrintReport}
                isLoading={PrintReportSelectAllMutation.isPending}
              />

              <CryovialReportModal
                isOpen={isCryovialModalOpen}
                onClose={() => setIsCryovialModalOpen(false)}
                selectedStudy={selectedStudy}
                onSubmit={executePrintReport}
                isLoading={PrintReportSelectAllMutation.isPending}
              />
          </>
        )}     
    </div>
  );
};

export default PrintLogsTabs;
